import React, { useEffect, useState } from 'react';
import {
  Drawer,
  DrawerTrigger,
  DrawerContent,
  DrawerBody,
  DrawerHeader,
  DrawerCloseButton,
  DrawerFooter,
} from '@fiverr-private/overlay';
import { I18n } from '@fiverr-private/i18n-react';
import { withQueryClient } from '@fiverr-private/query_client_hoc';
import { calculateBriefDisplayStatus, BriefDisplayStatus } from '@fiverr-private/dynamic_matching';
import { ErrorBoundary, renderChildren } from '@fiverr-private/ui_utils';
import { logger } from '@fiverr-private/obs';
import type { Currency } from '@fiverr-private/currencies_plugin';
import { buyerBriefPreviewDrawerIsShown } from '../../monitoring/bqEvents';
import { LoadingState } from '../Shared/LoadingState';
import { ErrorState } from '../Shared/ErrorState';
import { useFetchBuyerBrief } from '../hooks/useFetchBuyerBrief';
import type BuyerBriefResponse from '../../types/BuyerBriefResponse';
import { BuyerBriefOverview } from './BuyerBriefOverview';
import { Controls } from './Controls';

interface BuyerBriefDrawerProps {
  briefId: BuyerBriefResponse['id'];
  source?: string;
  hideFooter?: boolean;
  children: React.ReactNode;
  currency?: Currency;
}

const onBuyerBriefDrawerRenderError = (originalError: Error, pageName?: string) => {
  const err = new Error('Buyer brief overview drawer failed to render');
  logger.error(err, {
    cause: originalError,
    pageName,
  });
};

const BuyerBriefContent: React.FC<{
  brief: BuyerBriefResponse;
  hideFooter?: boolean;
  currency?: Currency;
  pageName?: string;
}> = ({ brief, hideFooter, currency, pageName = '' }) => {
  const { totalCount: offersCount, acceptedCount: hiredCount } = brief.customOffersMetrics || {};
  const hasPreviousInvites = !!offersCount;
  const briefDisplayStatus = calculateBriefDisplayStatus(
    brief?.tier,
    brief?.status,
    hasPreviousInvites,
    hiredCount,
    offersCount
  );
  const noActionsStatuses = [BriefDisplayStatus.INACTIVE, BriefDisplayStatus.REJECTED];

  useEffect(() => {
    buyerBriefPreviewDrawerIsShown({ pageName, briefId: brief.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <DrawerBody>
        <BuyerBriefOverview {...brief} currency={currency} />
      </DrawerBody>
      {!hideFooter && !noActionsStatuses.includes(briefDisplayStatus) && (
        <DrawerFooter>
          <Controls
            briefId={brief.id}
            status={brief.status}
            briefDisplayStatus={briefDisplayStatus}
            hasPreviousInvites={hasPreviousInvites}
          />
        </DrawerFooter>
      )}
    </>
  );
};

const BuyerBriefDrawer: React.FC<BuyerBriefDrawerProps> = ({ briefId, source, hideFooter, currency, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { data, isLoading, isError } = useFetchBuyerBrief({ briefId, enabled: isOpen });

  const childrenWithProps = React.Children.map(children, (child) =>
    renderChildren(child, { onClick: () => setIsOpen(true) })
  );

  return (
    <ErrorBoundary onError={(err) => onBuyerBriefDrawerRenderError(err, source)}>
      <Drawer isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <DrawerTrigger>{childrenWithProps}</DrawerTrigger>
        <DrawerContent size="lg">
          <DrawerHeader>
            <I18n k="brief_preview_drawer.buyer.title" />
            <DrawerCloseButton />
          </DrawerHeader>
          {isLoading && <LoadingState />}
          {!isLoading && (isError || !data) && <ErrorState />}
          {!isLoading && !isError && data && (
            <BuyerBriefContent brief={data} hideFooter={hideFooter} currency={currency} pageName={source} />
          )}
        </DrawerContent>
      </Drawer>
    </ErrorBoundary>
  );
};

const BuyerBriefDrawerWithQueryClient = withQueryClient(BuyerBriefDrawer);

export { BuyerBriefDrawerWithQueryClient as BuyerBriefDrawer };
